import { 
  TrackedEventPredictions,
  TrackedFightPrediction,
  PredictionHistoryData,
  FightPrediction, 
  PredictionData,
  EventData
} from '../types';
import { format } from 'date-fns';
import { getAvailableEvents, getPredictionDataForEvent } from './dataService';

// Constants and storage keys
export const PREDICTION_HISTORY_KEY = 'fight-vision-prediction-history-v2';
const MAX_STORAGE_SIZE_BYTES = 4 * 1024 * 1024; // 4MB safety limit

// Format date to ISO string without time
const formatDate = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

// Generate a unique ID - simplified version
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
};

// Check if localStorage is available and working
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = `test-storage-${Date.now()}`;
    localStorage.setItem(testKey, 'test');
    const testValue = localStorage.getItem(testKey);
    localStorage.removeItem(testKey);
    return testValue === 'test';
  } catch (e) {
    console.error('localStorage is not available:', e);
    return false;
  }
};

// Get size of serialized data
const getDataSize = (data: any): number => {
  try {
    return JSON.stringify(data).length;
  } catch (e) {
    return 0;
  }
};

// Create default history data structure
const createDefaultHistoryData = (): PredictionHistoryData => {
  return {
    allTimeStats: {
      totalFights: 0,
      correctPredictions: 0,
      successRate: 0,
      baselineCorrectPredictions: 0,
      baselineSuccessRate: 0
    },
    events: [],
    timeline: []
  };
};

// Initialize prediction history from localStorage or create empty structure
export const initializePredictionHistory = (): PredictionHistoryData => {
  if (!isLocalStorageAvailable()) {
    console.error('localStorage is not available, using default data');
    return createDefaultHistoryData();
  }
  
  try {
    console.log('Checking localStorage for key:', PREDICTION_HISTORY_KEY);
    const storedData = localStorage.getItem(PREDICTION_HISTORY_KEY);
    
    if (!storedData) {
      console.log('No prediction history found in localStorage, creating new history');
      return createDefaultHistoryData();
    }
    
    console.log('Found stored prediction history, size:', storedData.length, 'bytes');
    const history: PredictionHistoryData = JSON.parse(storedData);
    console.log('Current stored events:', history.events.map(e => ({
      name: e.eventName,
      date: e.eventDate,
      fights: e.fights.length,
      status: e.status
    })));
    
    return history;
  } catch (error) {
    console.error('Error loading prediction history:', error);
    return createDefaultHistoryData();
  }
};

// Save prediction history to localStorage with size optimization if needed
const savePredictionHistory = (data: PredictionHistoryData): boolean => {
  console.log('Attempting to save prediction history...');
  
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage not available, cannot save prediction history');
    return false;
  }

  try {
    // First attempt: Save complete data
    const serializedData = JSON.stringify(data);
    const dataSize = serializedData.length;
    
    console.log('Data to save:', {
      eventsCount: data.events.length,
      events: data.events.map(e => e.eventName),
      dataSize: dataSize + ' bytes'
    });
    
    if (dataSize > MAX_STORAGE_SIZE_BYTES) {
      console.warn(`Data size (${dataSize} bytes) exceeds safety limit, optimizing before saving`);
      return saveOptimizedHistory(data);
    }
    
    localStorage.setItem(PREDICTION_HISTORY_KEY, serializedData);
    console.log('Successfully wrote to localStorage');
    
    // Verify data was saved correctly by reading it back
    const verificationData = localStorage.getItem(PREDICTION_HISTORY_KEY);
    if (!verificationData) {
      console.error('Verification failed: Data was not saved to localStorage');
      return false;
    }
    
    // Parse and verify the data structure
    try {
      const parsedData = JSON.parse(verificationData) as PredictionHistoryData;
      if (!parsedData.events || !Array.isArray(parsedData.events)) {
        console.error('Verification failed: Saved data is not in correct format');
        return false;
      }
      
      console.log('Successfully verified saved data:', {
        eventsCount: parsedData.events.length,
        events: parsedData.events.map((e: TrackedEventPredictions) => e.eventName),
        bytes: dataSize
      });
      
      return true;
    } catch (parseError) {
      console.error('Verification failed: Could not parse saved data', parseError);
      return false;
    }
  } catch (error) {
    console.error('Failed to save prediction history to localStorage:', error);
    return saveOptimizedHistory(data);
  }
};

// Save optimized history data (reduced size)
const saveOptimizedHistory = (data: PredictionHistoryData): boolean => {
  try {
    // Create a copy with limited timeline to reduce size
    const reducedData = { 
      ...data,
      timeline: data.timeline.slice(-20) // Keep only most recent 20 points
    };
    
    const serializedData = JSON.stringify(reducedData);
    if (serializedData.length > MAX_STORAGE_SIZE_BYTES) {
      console.error('Even with optimizations, data is too large for localStorage');
      return false;
    }
    
    localStorage.setItem(PREDICTION_HISTORY_KEY, serializedData);
    
    // Verify data was saved correctly by reading it back
    const verificationData = localStorage.getItem(PREDICTION_HISTORY_KEY);
    if (!verificationData) {
      console.error('Verification failed: Optimized data was not saved to localStorage');
      return false;
    }
    
    // Parse and verify the data structure
    try {
      const parsedData = JSON.parse(verificationData) as PredictionHistoryData;
      if (!parsedData.events || !Array.isArray(parsedData.events)) {
        console.error('Verification failed: Saved optimized data is not in correct format');
        return false;
      }
      
      console.log('Successfully saved and verified optimized prediction history:', {
        eventsCount: parsedData.events.length,
        bytes: serializedData.length,
        timelinePoints: parsedData.timeline?.length || 0
      });
      
      return true;
    } catch (parseError) {
      console.error('Verification failed: Could not parse saved optimized data', parseError);
      return false;
    }
  } catch (error) {
    console.error('Failed to save optimized prediction history:', error);
    return false;
  }
};

// Get current prediction history
export const getPredictionHistory = (): PredictionHistoryData => {
  return initializePredictionHistory();
};

// Helper function to determine the favorite based on odds in the importantStats
const determineFavorite = (fight: TrackedFightPrediction): boolean | undefined => {
  // By default, fighter1 is favorite if predictedWinnerProbability > 50
  // But if we have odds data, we should use that instead
  if (fight.fighter1IsOddsFavorite !== undefined) {
    return fight.fighter1IsOddsFavorite;
  }
  
  // If we don't have explicit odds data, use our prediction as a fallback
  return fight.predictedWinnerProbability >= 50;
};

// Function to determine favorite from odds values
const determineFavoriteFromOdds = (fighter1Value: number, fighter2Value: number): boolean => {
  // Check if odds are in American format (at least one negative value)
  if (fighter1Value < 0 || fighter2Value < 0) {
    // If both are negative, the MORE negative number is the favorite
    if (fighter1Value < 0 && fighter2Value < 0) {
      // Fighter1 is favorite if they have the "more negative" (smaller) number
      return fighter1Value < fighter2Value;
    } 
    // If only fighter1 is negative, they're the favorite
    else if (fighter1Value < 0) {
      return true;
    } 
    // If only fighter2 is negative, they're the favorite
    else if (fighter2Value < 0) {
      return false;
    }
  }
  
  // For decimal odds or both positive American odds, the smaller number is the favorite
  return fighter1Value < fighter2Value;
};

// Update the verifyEventResults function to track baseline performance
export const verifyEventResults = (
  eventId: string,
  results: { fightId: string, winnerName: string }[]
): boolean => {
  console.log('Starting verification process for event:', eventId);
  console.log('Results to verify:', results);
  
  if (!isLocalStorageAvailable()) {
    console.error('Cannot verify results: localStorage is not available');
    return false;
  }
  
  const history = initializePredictionHistory();
  const currentDate = formatDate(new Date());
  
  // Find the event in history
  const eventIndex = history.events.findIndex(event => event.eventId === eventId);
  
  if (eventIndex === -1) {
    console.error(`Event with ID ${eventId} not found in prediction history`);
    return false;
  }
  
  console.log(`Found event at index ${eventIndex}:`, history.events[eventIndex].eventName);
  const event = history.events[eventIndex];
  let correctCount = 0;
  let incorrectCount = 0;
  let baselineCorrectCount = 0;
  let baselineIncorrectCount = 0;
  let updatedFights = 0;
  
  // Update each fight with actual results
  results.forEach(result => {
    const fightIndex = event.fights.findIndex(fight => fight.fightId === result.fightId);
    
    if (fightIndex !== -1) {
      console.log(`Updating fight ${result.fightId} with winner: ${result.winnerName}`);
      const fight = event.fights[fightIndex];
      
      // Update fight with result
      fight.actualWinnerName = result.winnerName;
      fight.wasCorrect = fight.predictedWinnerName === result.winnerName;
      fight.verificationDate = currentDate;
      updatedFights++;
      
      // Count for model's prediction summary
      if (fight.wasCorrect) {
        correctCount++;
      } else {
        incorrectCount++;
      }
      
      // BASELINE CALCULATION:
      // Get the favorite based on stored odds data
      if (fight.fighter1IsOddsFavorite !== undefined) {
        // We have the odds data stored with the fight
        const favoriteWinnerName = fight.fighter1IsOddsFavorite ? fight.fighter1Name : fight.fighter2Name;
        const oddsBasedWinnerCorrect = favoriteWinnerName === result.winnerName;
        
        // Store the odds-based result in the fight object
        fight.oddsBasedWinnerCorrect = oddsBasedWinnerCorrect;
        
        // Count for baseline summary
        if (oddsBasedWinnerCorrect) {
          baselineCorrectCount++;
          console.log(`Baseline was correct: favorite ${favoriteWinnerName} won`);
        } else {
          baselineIncorrectCount++;
          console.log(`Baseline was wrong: favorite ${favoriteWinnerName} lost to ${result.winnerName}`);
        }
      } else {
        console.warn(`No odds data available for fight ${fight.fighter1Name} vs ${fight.fighter2Name}, cannot calculate baseline`);
        // We don't have odds data, so we can't update the baseline
        fight.oddsBasedWinnerCorrect = null;
      }
      
      console.log(`Fight prediction was ${fight.wasCorrect ? 'correct' : 'incorrect'}, baseline was ${fight.oddsBasedWinnerCorrect === true ? 'correct' : fight.oddsBasedWinnerCorrect === false ? 'incorrect' : 'unknown'}`);
    } else {
      console.error(`Fight with ID ${result.fightId} not found in event`);
    }
  });
  
  if (updatedFights === 0) {
    console.error('No fights were updated during verification');
    return false;
  }
  
  // Update event summary
  const existingCorrect = event.summary.correct || 0;
  const existingIncorrect = event.summary.incorrect || 0;
  const existingBaselineCorrect = event.summary.baselineCorrect || 0;
  const existingBaselineIncorrect = event.summary.baselineIncorrect || 0;
  
  event.summary.correct = existingCorrect + correctCount;
  event.summary.incorrect = existingIncorrect + incorrectCount;
  event.summary.pending = event.fights.length - (event.summary.correct + event.summary.incorrect);
  
  // Update baseline summary
  event.summary.baselineCorrect = existingBaselineCorrect + baselineCorrectCount;
  event.summary.baselineIncorrect = existingBaselineIncorrect + baselineIncorrectCount;
  
  // Calculate success rates
  const totalVerified = event.summary.correct + event.summary.incorrect;
  if (totalVerified > 0) {
    event.summary.successRate = (event.summary.correct / totalVerified) * 100;
    
    // Only calculate baseline success rate if we have baseline data
    const totalBaselineVerified = event.summary.baselineCorrect + event.summary.baselineIncorrect;
    if (totalBaselineVerified > 0) {
      event.summary.baselineSuccessRate = (event.summary.baselineCorrect / totalBaselineVerified) * 100;
    } else {
      event.summary.baselineSuccessRate = 0;
    }
  } else {
    event.summary.successRate = 0;
    event.summary.baselineSuccessRate = 0;
  }
  
  // Set verification date if it doesn't exist
  if (!event.verificationDate) {
    event.verificationDate = currentDate;
  }
  
  console.log('Updated event summary:', {
    correct: event.summary.correct,
    incorrect: event.summary.incorrect,
    baselineCorrect: event.summary.baselineCorrect,
    baselineIncorrect: event.summary.baselineIncorrect,
    pending: event.summary.pending,
    successRate: event.summary.successRate.toFixed(2) + '%',
    baselineSuccessRate: event.summary.baselineSuccessRate.toFixed(2) + '%'
  });

  // Update all-time stats
  let totalVerifiedFights = 0;
  let totalCorrectPredictions = 0;
  let totalBaselineCorrectPredictions = 0;
  let totalBaselineVerifiedFights = 0;
  
  // Recalculate all stats from the events to ensure consistency
  history.events.forEach(evt => {
    const eventVerified = (evt.summary.correct + evt.summary.incorrect);
    totalVerifiedFights += eventVerified;
    totalCorrectPredictions += evt.summary.correct;
    
    const eventBaselineVerified = (evt.summary.baselineCorrect + evt.summary.baselineIncorrect);
    totalBaselineVerifiedFights += eventBaselineVerified;
    totalBaselineCorrectPredictions += evt.summary.baselineCorrect || 0;
  });
  
  history.allTimeStats.totalFights = history.events.reduce((sum, evt) => sum + evt.fights.length, 0);
  history.allTimeStats.correctPredictions = totalCorrectPredictions;
  history.allTimeStats.baselineCorrectPredictions = totalBaselineCorrectPredictions;
  
  if (totalVerifiedFights > 0) {
    history.allTimeStats.successRate = (totalCorrectPredictions / totalVerifiedFights) * 100;
  } else {
    history.allTimeStats.successRate = 0;
  }
  
  // Calculate baseline success rate separately
  if (totalBaselineVerifiedFights > 0) {
    history.allTimeStats.baselineSuccessRate = (totalBaselineCorrectPredictions / totalBaselineVerifiedFights) * 100;
  } else {
    history.allTimeStats.baselineSuccessRate = 0;
  }
  
  console.log('Updated all-time stats:', {
    totalFights: history.allTimeStats.totalFights,
    verifiedFights: totalVerifiedFights,
    baselineVerifiedFights: totalBaselineVerifiedFights,
    correctPredictions: history.allTimeStats.correctPredictions,
    baselineCorrectPredictions: history.allTimeStats.baselineCorrectPredictions,
    successRate: history.allTimeStats.successRate.toFixed(2) + '%',
    baselineSuccessRate: history.allTimeStats.baselineSuccessRate.toFixed(2) + '%'
  });
  
  // Add new timeline point for significant verifications
  if (correctCount + incorrectCount > 0) {
    const timelinePoint = {
      date: currentDate,
      successRate: history.allTimeStats.successRate,
      totalPredictions: history.allTimeStats.totalFights,
      correctPredictions: history.allTimeStats.correctPredictions,
      baselineSuccessRate: history.allTimeStats.baselineSuccessRate,
      baselineCorrectPredictions: history.allTimeStats.baselineCorrectPredictions
    };
    
    history.timeline.push(timelinePoint);
    console.log('Added new timeline point:', timelinePoint);
  }
  
  // Save updated history
  const saveSuccess = savePredictionHistory(history);
  if (!saveSuccess) {
    console.error('Failed to save prediction history after verification');
    return false;
  }
  
  console.log('Successfully completed verification process');
  return true;
};

// Helper to get total pending fights across all events
const getTotalPendingFights = (history: PredictionHistoryData): number => {
  return history.events.reduce((total, event) => {
    return total + event.summary.pending;
  }, 0);
};

// Clear prediction history (for testing/debugging)
export const clearPredictionHistory = (): void => {
  if (isLocalStorageAvailable()) {
    localStorage.removeItem(PREDICTION_HISTORY_KEY);
    console.log('Cleared prediction history from storage');
  }
};

// Force reload prediction history from storage
export const reloadPredictionHistory = (): PredictionHistoryData => {
  // Force a clean reload from storage
  if (isLocalStorageAvailable()) {
    // Clear any cached version first
    const history = initializePredictionHistory();
    console.log('Forced reload of prediction history from storage', {
      events: history.events.length,
      successRate: history.allTimeStats.successRate.toFixed(2) + '%'
    });
    return history;
  } else {
    console.warn('Cannot reload history: localStorage is not available');
    return createDefaultHistoryData();
  }
};

// Find events needing verification based on date
export const findEventsNeedingVerification = (): TrackedEventPredictions[] => {
  const history = initializePredictionHistory();
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
  
  return history.events
    .filter(event => {
      // Event with pending fights
      const hasPendingFights = event.summary.pending > 0;
      
      // Event date has passed (including today)
      const eventDate = new Date(event.eventDate);
      eventDate.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      const eventHasPassed = eventDate <= now;
      
      return hasPendingFights && eventHasPassed;
    })
    .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());
};

// Update the recordEventPredictions function to track the odds favorite
export const recordEventPredictions = (
  eventName: string, 
  eventDate: string,
  predictions: FightPrediction[],
  predictionInputs?: PredictionData,
  integratedData?: EventData,
  status?: 'pending' | 'completed'
): boolean => {
  const history = initializePredictionHistory();
  const currentDate = formatDate(new Date());
  
  // Check if this event already exists by name
  const existingEventIndex = history.events.findIndex(
    event => event.eventName.toLowerCase() === eventName.toLowerCase()
  );
  
  // If the event already exists and status hasn't changed, don't update
  if (existingEventIndex >= 0) {
    const existingEvent = history.events[existingEventIndex];
    if (existingEvent.status === status) {
      console.log(`Event ${eventName} already exists with same status, skipping update`);
      return false;
    }
    
    // If status is changing from pending to completed, update it
    if (existingEvent.status === 'pending' && status === 'completed') {
      console.log(`Updating event ${eventName} from pending to completed`);
      existingEvent.status = 'completed';
      return savePredictionHistory(history);
    }
    return false;
  }
  
  console.log(`Recording new event predictions for: ${eventName}`);
  const eventId = generateId();
  
  // Create tracked fights with proper status
  const trackedFights: TrackedFightPrediction[] = predictions.map(prediction => {
    // Find matching fight from integrated data if available
    const matchingFight = integratedData?.fights?.find(f => 
      f.fighter1_name === prediction.fighter1.name && 
      f.fighter2_name === prediction.fighter2.name
    );

    return {
      fightId: generateId(),
      eventId,
      fighter1Name: prediction.fighter1.name,
      fighter2Name: prediction.fighter2.name,
      predictedWinnerName: prediction.fighter1.winProbability > prediction.fighter2.winProbability ? 
        prediction.fighter1.name : prediction.fighter2.name,
      predictedWinnerProbability: Math.max(prediction.fighter1.winProbability, prediction.fighter2.winProbability) * 100,
      actualWinnerName: null,
      wasCorrect: null,
      weightClass: matchingFight?.weight_class || 'Unknown',
      predictionDate: currentDate,
      verificationDate: null,
      fighter1IsOddsFavorite: undefined,
      oddsBasedWinnerCorrect: null
    };
  });
  
  // Create new event with proper initialization
  const newEvent: TrackedEventPredictions = {
    eventId,
    eventName,
    eventDate,
    predictionDate: currentDate,
    verificationDate: null,
    fights: trackedFights,
    summary: {
      correct: 0,
      incorrect: 0,
      pending: trackedFights.length,
      successRate: 0,
      baselineCorrect: 0,
      baselineIncorrect: 0,
      baselineSuccessRate: 0
    },
    isPlaceholder: false,
    status: status || 'pending'
  };
  
  // Add the new event to history
  history.events.push(newEvent);
  
  // Recalculate stats
  recalculateHistoryStats(history);
  
  // Save updated history using the main save function
  const saveSuccess = savePredictionHistory(history);
  
  if (!saveSuccess) {
    console.error('Failed to save prediction history in recordEventPredictions');
    return false;
  }
  
  // Verify the save worked by reading it back
  const verifyHistory = initializePredictionHistory();
  if (!verifyHistory.events.find(e => e.eventId === eventId)) {
    console.error('Verification failed: Newly added event not found in saved history');
    return false;
  }
  
  // Dispatch event to notify UI of update
  const event = new CustomEvent('predictionHistoryUpdated', {
    detail: { eventId, eventName, status }
  });
  window.dispatchEvent(event);
  
  console.log('Successfully recorded and verified new event predictions:', {
    eventName,
    eventId,
    fights: trackedFights.length,
    status
  });
  
  return true;
};

// Helper function to save history (to avoid direct references to localStorage)
const saveHistory = (history: PredictionHistoryData): void => {
  try {
    if (!isLocalStorageAvailable()) {
      console.error('localStorage is not available for saving prediction history');
      return;
    }
    
    const serialized = JSON.stringify(history);
    const dataSize = getDataSize(history);
    
    if (dataSize > MAX_STORAGE_SIZE_BYTES) {
      console.warn(`Prediction history exceeds safe size limit (${dataSize} bytes). Some data may be trimmed.`);
      // Implement trimming logic if needed
    }
    
    localStorage.setItem(PREDICTION_HISTORY_KEY, serialized);
    console.log(`Saved prediction history with ${history.events.length} events`);
  } catch (e) {
    console.error('Failed to save prediction history:', e);
  }
};

// Add a function to remove specific placeholder events
export const removeEventsByName = (eventNames: string[]): boolean => {
  console.log('Removing events by name:', eventNames);
  
  if (!isLocalStorageAvailable()) {
    console.error('Cannot remove events: localStorage is not available');
    return false;
  }
  
  try {
    // Get current history
    const history = initializePredictionHistory();
    
    // Count events before removal
    const originalCount = history.events.length;
    
    // Filter out the events with matching names
    history.events = history.events.filter(event => !eventNames.includes(event.eventName));
    
    // Count events after removal
    const newCount = history.events.length;
    const removedCount = originalCount - newCount;
    
    console.log(`Removed ${removedCount} events from history`);
    
    if (removedCount > 0) {
      // Recalculate all stats
      recalculateHistoryStats(history);
      
      // Save updated history
      const saveSuccess = savePredictionHistory(history);
      if (!saveSuccess) {
        console.error('Failed to save history after removing events');
        return false;
      }
      
      console.log('Successfully saved history after removing events');
      return true;
    } else {
      console.log('No events were removed (events not found)');
      return false;
    }
  } catch (error) {
    console.error('Error removing events:', error);
    return false;
  }
};

// Helper function to recalculate all history stats
const recalculateHistoryStats = (history: PredictionHistoryData): void => {
  console.log('Recalculating all history statistics');
  
  let totalFights = 0;
  let correctPredictions = 0;
  let verifiedFights = 0;
  let baselineCorrectPredictions = 0;
  
  // Count all fights, correct predictions, and verified fights
  history.events.forEach(event => {
    totalFights += event.fights.length;
    verifiedFights += (event.summary.correct + event.summary.incorrect);
    correctPredictions += event.summary.correct;
    baselineCorrectPredictions += event.summary.baselineCorrect || 0;
  });
  
  // Update all-time stats
  history.allTimeStats.totalFights = totalFights;
  history.allTimeStats.correctPredictions = correctPredictions;
  history.allTimeStats.baselineCorrectPredictions = baselineCorrectPredictions;
  
  if (verifiedFights > 0) {
    history.allTimeStats.successRate = (correctPredictions / verifiedFights) * 100;
    history.allTimeStats.baselineSuccessRate = (baselineCorrectPredictions / verifiedFights) * 100;
  } else {
    history.allTimeStats.successRate = 0;
    history.allTimeStats.baselineSuccessRate = 0;
  }
  
  // Only initialize timeline if it doesn't exist
  if (!history.timeline || !Array.isArray(history.timeline)) {
    const currentDate = formatDate(new Date());
    history.timeline = [{
      date: currentDate,
      successRate: history.allTimeStats.successRate,
      totalPredictions: totalFights,
      correctPredictions: correctPredictions,
      baselineSuccessRate: history.allTimeStats.baselineSuccessRate,
      baselineCorrectPredictions: baselineCorrectPredictions
    }];
  }
  
  console.log('Recalculated history stats:', {
    events: history.events.length,
    totalFights,
    correctPredictions,
    successRate: history.allTimeStats.successRate.toFixed(2) + '%',
    timelinePoints: history.timeline.length
  });
};

// Also clean up the addTestFight function
export const addTestFight = (): void => {
  // This function is now a no-op
  console.log('addTestFight called but is disabled');
};

// Add a function to reset all verifications and keep only most recent upcoming event
export const resetToMostRecentEvent = (): boolean => {
  console.log("Resetting prediction history to only contain the most recent event");
  
  if (!isLocalStorageAvailable()) {
    console.error('Cannot reset: localStorage is not available');
    return false;
  }
  
  try {
    const history = initializePredictionHistory();
    
    // Filter out any test events first
    history.events = history.events.filter(event => !event.eventName?.includes('UFC Test Event'));
    
    if (history.events.length === 0) {
      console.log("No events found to reset, keeping empty history");
      return savePredictionHistory(createDefaultHistoryData());
    }
    
    // Sort events by date (newest first)
    history.events.sort((a, b) => {
      const dateA = new Date(a.eventDate || a.predictionDate);
      const dateB = new Date(b.eventDate || b.predictionDate);
      return dateB.getTime() - dateA.getTime();
    });
    
    console.log(`Found ${history.events.length} events, keeping only the most recent one: ${history.events[0].eventName}`);
    
    // Keep only the most recent event
    const mostRecentEvent = history.events[0];
    history.events = [mostRecentEvent];
    
    // Reset verification state in this event
    mostRecentEvent.verificationDate = null;
    mostRecentEvent.summary = {
        correct: 0,
        incorrect: 0,
      pending: mostRecentEvent.fights.length,
      successRate: 0,
      baselineCorrect: 0,
      baselineIncorrect: 0,
      baselineSuccessRate: 0
    };
    
    // Reset all fights to unverified state
    mostRecentEvent.fights.forEach(fight => {
      fight.actualWinnerName = null;
      fight.wasCorrect = null;
      fight.verificationDate = null;
      fight.oddsBasedWinnerCorrect = null; // Reset baseline tracking
    });
    
    // Reset all global stats
    history.allTimeStats = {
      totalFights: history.events.length > 0 
        ? history.events[0].fights.length 
        : 0,
      correctPredictions: 0,
      successRate: 0,
      baselineCorrectPredictions: 0,
      baselineSuccessRate: 0
    };
    
    // Reset timeline
    if (history.events.length > 0) {
      const currentDate = formatDate(new Date());
      history.timeline = [{
        date: currentDate,
        successRate: 0,
        totalPredictions: history.allTimeStats.totalFights,
        correctPredictions: 0,
        baselineSuccessRate: 0,
        baselineCorrectPredictions: 0
      }];
    } else {
      history.timeline = [];
    }
    
    console.log("Reset complete, saving modified history");
    return savePredictionHistory(history);
    
  } catch (error) {
    console.error('Error resetting prediction history:', error);
    return false;
  }
};

// Add a complete reset function that wipes all prediction history
export const completeReset = (): boolean => {
  console.log("Performing a complete reset of all prediction history");
  
  if (!isLocalStorageAvailable()) {
    console.error('Cannot reset: localStorage is not available');
    return false;
  }
  
  try {
    // Create a fresh empty history
    const freshHistory = createDefaultHistoryData();
    
    // Save it to localStorage
    const saveSuccess = savePredictionHistory(freshHistory);
    if (!saveSuccess) {
      console.error('Failed to save empty history after reset');
      return false;
    }
    
    console.log("Complete reset successful - all prediction history has been cleared");
    return true;
  } catch (error) {
    console.error('Error performing complete reset:', error);
    return false;
  }
};

// Update an existing verification for a fight
export const updateFightVerification = (
  eventId: string,
  fightId: string,
  newWinnerName: string
): boolean => {
  console.log(`Updating verification for fight ${fightId} in event ${eventId}, new winner: ${newWinnerName}`);
  
  if (!isLocalStorageAvailable()) {
    console.error('Cannot update verification: localStorage is not available');
    return false;
  }
  
  const history = initializePredictionHistory();
  const currentDate = formatDate(new Date());
  
  // Find the event in history
  const eventIndex = history.events.findIndex(event => event.eventId === eventId);
  
  if (eventIndex === -1) {
    console.error(`Event with ID ${eventId} not found in prediction history`);
    return false;
  }
  
  console.log(`Found event at index ${eventIndex}:`, history.events[eventIndex].eventName);
  const event = history.events[eventIndex];
  
  // Find the fight
  const fightIndex = event.fights.findIndex(fight => fight.fightId === fightId);
  
  if (fightIndex === -1) {
    console.error(`Fight with ID ${fightId} not found in event`);
    return false;
  }
  
  const fight = event.fights[fightIndex];
  const oldWinnerName = fight.actualWinnerName;
  const wasCorrectBefore = fight.wasCorrect;
  
  // Update the fight with the new winner
  fight.actualWinnerName = newWinnerName;
  fight.wasCorrect = fight.predictedWinnerName === newWinnerName;
  fight.verificationDate = currentDate;
  
  // Calculate the change in stats
  let correctDelta = 0;
  let incorrectDelta = 0;
  
  // If previously correct and now incorrect
  if (wasCorrectBefore && !fight.wasCorrect) {
    correctDelta = -1;
    incorrectDelta = 1;
  } 
  // If previously incorrect and now correct
  else if (!wasCorrectBefore && fight.wasCorrect) {
    correctDelta = 1;
    incorrectDelta = -1;
  }
  
  // Adjust the event summary
  if (correctDelta !== 0) {
    event.summary.correct += correctDelta;
    event.summary.incorrect += incorrectDelta;
    event.summary.successRate = event.summary.correct / (event.summary.correct + event.summary.incorrect) * 100;
  }
  
  // Update the history's all-time stats
  if (correctDelta !== 0) {
    history.allTimeStats.correctPredictions += correctDelta;
    // Success rate is recalculated in the full history
  }
  
  // Recalculate all history stats to ensure consistency
  recalculateHistoryStats(history);
  
  // Add a new timeline point for this update
  const totalFights = history.events.reduce((sum, event) => sum + event.fights.length, 0);
  const verifiedFights = totalFights - getTotalPendingFights(history);
  const totalCorrect = history.events.reduce((sum, event) => sum + event.summary.correct, 0);
  
  if (verifiedFights > 0) {
    const newTimelinePoint = {
      date: currentDate,
      successRate: (totalCorrect / verifiedFights) * 100,
      totalFights: verifiedFights,
      totalPredictions: verifiedFights,
      correctPredictions: totalCorrect,
      baselineSuccessRate: history.allTimeStats.baselineSuccessRate,
      baselineCorrectPredictions: history.allTimeStats.baselineCorrectPredictions
    };
    
    history.timeline.push(newTimelinePoint);
  }
  
  // Save updated history
  const saveSuccess = savePredictionHistory(history);
  if (!saveSuccess) {
    console.error('Failed to save updated prediction history');
    return false;
  }
  
  console.log(`Successfully updated verification for fight ${fightId}, changed from ${oldWinnerName} to ${newWinnerName}`);
  return true;
};

// Update model's prediction for a fight
export const updateModelPrediction = (
  eventId: string,
  fightId: string,
  newPredictedWinner: string,
  newPredictedProbability: number
): boolean => {
  console.log(`Updating model prediction for fight ${fightId} in event ${eventId}, new winner: ${newPredictedWinner}, probability: ${newPredictedProbability}%`);
  
  if (!isLocalStorageAvailable()) {
    console.error('Cannot update prediction: localStorage is not available');
    return false;
  }
  
  const history = initializePredictionHistory();
  const currentDate = formatDate(new Date());
  
  // Find the event in history
  const eventIndex = history.events.findIndex(event => event.eventId === eventId);
  
  if (eventIndex === -1) {
    console.error(`Event with ID ${eventId} not found in prediction history`);
    return false;
  }
  
  console.log(`Found event at index ${eventIndex}:`, history.events[eventIndex].eventName);
  const event = history.events[eventIndex];
  
  // Find the fight
  const fightIndex = event.fights.findIndex(fight => fight.fightId === fightId);
  
  if (fightIndex === -1) {
    console.error(`Fight with ID ${fightId} not found in event`);
    return false;
  }
  
  const fight = event.fights[fightIndex];
  const oldPredictedWinner = fight.predictedWinnerName;
  const oldPredictedProbability = fight.predictedWinnerProbability;
  
  // Store old values for logging
  console.log(`Previous prediction: ${oldPredictedWinner} (${oldPredictedProbability}%) vs New prediction: ${newPredictedWinner} (${newPredictedProbability}%)`);
  
  // Update the fight prediction
  fight.predictedWinnerName = newPredictedWinner;
  fight.predictedWinnerProbability = newPredictedProbability;
  fight.predictionDate = currentDate; // Update the prediction date
  
  // If this fight was already verified, we need to update wasCorrect
  if (fight.actualWinnerName !== null) {
    const wasCorrectBefore = fight.wasCorrect;
    fight.wasCorrect = fight.predictedWinnerName === fight.actualWinnerName;
    
    // If correctness changed, update event stats
    if (wasCorrectBefore !== fight.wasCorrect) {
      let correctDelta = 0;
      let incorrectDelta = 0;
      
      // If changed from incorrect to correct
      if (!wasCorrectBefore && fight.wasCorrect) {
        correctDelta = 1;
        incorrectDelta = -1;
      } 
      // If changed from correct to incorrect
      else if (wasCorrectBefore && !fight.wasCorrect) {
        correctDelta = -1;
        incorrectDelta = 1;
      }
      
      // Update event summary
      if (correctDelta !== 0) {
        event.summary.correct += correctDelta;
        event.summary.incorrect += incorrectDelta;
        
        const totalVerified = event.summary.correct + event.summary.incorrect;
        if (totalVerified > 0) {
          event.summary.successRate = (event.summary.correct / totalVerified) * 100;
        }
      }
    }
  }
  
  // Recalculate all history stats to ensure consistency
  recalculateHistoryStats(history);
  
  // Always add a new timeline point to ensure chart updates
  // This is a key change to ensure the chart gets updated
  const totalFights = history.events.reduce((sum, event) => sum + event.fights.length, 0);
  const verifiedFights = totalFights - getTotalPendingFights(history);
  const totalCorrect = history.events.reduce((sum, event) => sum + event.summary.correct, 0);
  
  // Create a new timeline point regardless of verified fights
  const newTimelinePoint = {
    date: currentDate,
    successRate: verifiedFights > 0 ? (totalCorrect / verifiedFights) * 100 : history.allTimeStats.successRate,
    totalPredictions: totalFights,
    totalFights: totalFights,
    correctPredictions: totalCorrect,
    baselineSuccessRate: history.allTimeStats.baselineSuccessRate,
    baselineCorrectPredictions: history.allTimeStats.baselineCorrectPredictions
  };
  
  history.timeline.push(newTimelinePoint);
  console.log('Added new timeline point for model update:', newTimelinePoint);
  
  // Save updated history
  const saveSuccess = savePredictionHistory(history);
  if (!saveSuccess) {
    console.error('Failed to save updated prediction history');
    return false;
  }
  
  console.log(`Successfully updated prediction for fight ${fightId}, changed from ${oldPredictedWinner} (${oldPredictedProbability}%) to ${newPredictedWinner} (${newPredictedProbability}%)`);
  return true;
};

// Add test function to check localStorage directly
export const debugStorage = (): void => {
  console.log('=== DEBUG STORAGE ===');
  console.log('localStorage available:', isLocalStorageAvailable());
  
  try {
    const storedData = localStorage.getItem(PREDICTION_HISTORY_KEY);
    if (!storedData) {
      console.log('No data found in localStorage');
      return;
    }
    
    console.log('Raw data size:', storedData.length, 'bytes');
    const parsedData = JSON.parse(storedData) as PredictionHistoryData;
    console.log('Events in storage:', parsedData.events.map((e: TrackedEventPredictions) => ({
      name: e.eventName,
      date: e.eventDate,
      fights: e.fights.length,
      status: e.status
    })));
  } catch (error) {
    console.error('Error reading localStorage:', error);
  }
  console.log('=== END DEBUG ===');
};

// Add test past event (for development/testing only)
export const addTestPastEvent = async (): Promise<void> => {
  try {
    // Get all available events from localStorage
    const eventsIndex = getAvailableEvents();
    if (!eventsIndex || eventsIndex.length === 0) {
      console.error('No past events available in localStorage');
      return;
    }
    
    // Find the most recent past event
    const pastEvent = eventsIndex
      .filter(eventName => !eventName.includes('Edwards vs. Brady')) // Exclude current event
      .sort()
      .pop();
    
    if (!pastEvent) {
      console.error('No suitable past event found');
      return;
    }
    
    console.log('Found past event:', pastEvent);
    
    // Get the prediction data for this event
    const predictionData = getPredictionDataForEvent(pastEvent);
    if (!predictionData || !predictionData.event_name || !predictionData.fights) {
      console.error('No prediction data available for past event');
      return;
    }
    
    console.log('Retrieved prediction data for past event:', predictionData);
    
    // First, remove the existing event if it exists
    const history = initializePredictionHistory();
    const existingEventIndex = history.events.findIndex(
      event => event.eventName.toLowerCase() === predictionData.event_name.toLowerCase()
    );
    
    if (existingEventIndex >= 0) {
      console.log(`Removing existing event: ${predictionData.event_name}`);
      history.events.splice(existingEventIndex, 1);
      savePredictionHistory(history);
    }
    
    // Record the event predictions
    const success = recordEventPredictions(
      predictionData.event_name,
      new Date().toISOString().split('T')[0], // Use today's date for test event
      predictionData.fights.map((fight: any) => ({
        fighter1: {
          name: fight.fighter1_name,
          winProbability: fight.fighter1_win_probability || 0.5,
          record: fight.fighter1_record || '0-0-0'
        },
        fighter2: {
          name: fight.fighter2_name,
          winProbability: fight.fighter2_win_probability || 0.5,
          record: fight.fighter2_record || '0-0-0'
        },
        explanation: fight.explanation || 'Model prediction based on fighter stats and odds',
        importantStats: [
          {
            name: 'Strikes per Minute',
            fighter1Value: fight.fighter1_stats?.SLPM || 0,
            fighter2Value: fight.fighter2_stats?.SLPM || 0,
            unit: 'SLPM'
          },
          {
            name: 'Strike Accuracy',
            fighter1Value: fight.fighter1_stats?.str_acc || 0,
            fighter2Value: fight.fighter2_stats?.str_acc || 0,
            unit: '%'
          },
          {
            name: 'Takedown Defense',
            fighter1Value: fight.fighter1_stats?.TDD || 0,
            fighter2Value: fight.fighter2_stats?.TDD || 0,
            unit: '%'
          }
        ]
      })),
      undefined,
      predictionData,
      'completed'
    );
    
    if (success) {
      console.log(`Added past event: ${predictionData.event_name}`);
      // Dispatch event to notify UI
      const event = new CustomEvent('predictionHistoryUpdated', {
        detail: { eventName: predictionData.event_name, status: 'completed' }
      });
      window.dispatchEvent(event);
    } else {
      console.error('Failed to add past event');
    }
  } catch (error) {
    console.error('Error adding past event:', error);
  }
}; 